import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Bulk publish of the desk's FX rate book from its own spreadsheet.
 *
 * The book lives in one sheet — a banner row, a header row and exactly ONE data
 * row holding every pair the desk quotes. Publishing it used to mean one
 * `POST /api/fxrates` per pair, which for ten pairs is ten calls and ten
 * chances to transpose a digit.
 *
 * Parse and publish are two separate calls on purpose:
 *  - **parse persists nothing.** The sheet can be re-uploaded as often as the
 *    desk likes, and what comes back is editable before anything is committed.
 *  - **publish takes rows, not a file.** Parsed rows and hand-typed rows go
 *    through identical validation, so a pair the parser missed can be added by
 *    hand without a second upload.
 *
 * Two things about the shape are easy to get wrong:
 *  - **The desk's `buy` is the `bid` and its `sell` is the `ask`.** That
 *    ordering is what satisfies the `ask >= bid` rule the single-rate endpoint
 *    already enforces. A sheet with the columns the other way round comes back
 *    `ASK_BELOW_BID` rather than being silently inverted.
 *  - **Bare `USD buy` columns are quoted against the BANNER currency.** The
 *    parse response echoes what it resolved as `quoteCurrency`; showing it is
 *    the difference between publishing USD/AMD and publishing something else.
 *
 * Both endpoints take the same roles that can already publish a single rate:
 * admin, funding, dealing, backoffice.
 */

// --- parse -----------------------------------------------------------------

/** Decoded size cap (`fxRates.maxFileBytes`). The reference workbook is ~10 KB. */
export const FX_RATES_MAX_FILE_BYTES = 500 * 1024;

export interface ParseFxRatesPayload {
  /** Base64 of the .xlsx. There is no multipart endpoint anywhere in this app. */
  file: string;
  /** Optional; echoed back on `sourceFile.name`. */
  fileName?: string;
}

export interface FxRatesSourceFile {
  name?: string;
  sha256?: string;
  /** How many complete pairs the parser read — NOT how many columns it saw. */
  pairCount?: number;
}

/**
 * One pair read off the sheet.
 *
 * `label`, `bidColumn` and `askColumn` are diagnostics — the sheet's own header
 * text and the columns the numbers came from — and exist to point an operator
 * at the offending cell. The publish call accepts and ignores them, so a parse
 * row can be posted through unmodified.
 */
export interface FxRateParsedPair {
  base: string;
  quote: string;
  /** The desk's BUY price. */
  bid: number;
  /** The desk's SELL price. Must be >= bid. */
  ask: number;
  label?: string;
  bidColumn?: string;
  askColumn?: string;
}

/**
 * A column that looks like a rate column but could not be read. Normally empty.
 * Non-empty means THOSE PAIRS WILL NOT BE PUBLISHED — surface it, never hide it.
 */
export interface FxRateUnparsedColumn {
  /** Prose, not a contract. Rendered as-is; there is no code to branch on. */
  reason?: string;
  /** The header text, and the column it sat in, so the cell can be found. */
  label?: string;
  column?: string;
  [key: string]: unknown;
}

export type FxRatesWarningCode =
  | 'STALE_FILE_DATE'
  | 'FUTURE_FILE_DATE'
  | 'NO_FILE_DATE';

/**
 * A date warning. It NEVER blocks — the desk sometimes republishes a previous
 * day's book deliberately and the backend does not overrule that. The operator
 * seeing the banner is the whole guard, so it has to be hard to miss.
 */
export interface FxRatesWarning {
  code: FxRatesWarningCode;
  /** The sheet's own `On Date` cell. */
  fileDate?: string | null;
  /** Today in the DESK's timezone (Asia/Yerevan), not UTC. */
  today?: string;
  message?: string;
  [key: string]: unknown;
}

export interface ParseFxRatesResponse {
  sourceFile?: FxRatesSourceFile;
  /**
   * What bare `USD buy` columns were resolved against, read from the banner's
   * `to <CUR>` and falling back to AMD. Always display it.
   */
  quoteCurrency?: string;
  fileDate?: string | null;
  today?: string;
  isStale?: boolean;
  pairs?: FxRateParsedPair[];
  unparsed?: FxRateUnparsedColumn[];
  warnings?: FxRatesWarning[];
}

// --- publish ---------------------------------------------------------------

/** The only fields the batch endpoint accepts. Any other key is REJECTED. */
export interface FxRateRow {
  base: string;
  quote: string;
  bid: number;
  ask: number;
}

export interface BatchFxRatesPayload {
  rates: FxRateRow[];
}

export interface AppliedFxRate {
  id?: string;
  base?: string;
  quote?: string;
  bid?: number;
  ask?: number;
  isCurrent?: boolean;
}

export interface BatchFxRatesResponse {
  applied?: AppliedFxRate[];
  count?: number;
}

export type FxRatesBlockerCode =
  | 'SAME_CURRENCY'
  | 'UNKNOWN_CURRENCY'
  | 'INVALID_RATE'
  | 'ASK_BELOW_BID'
  | 'DUPLICATE_PAIR'
  | 'REVERSE_PAIR_EXISTS';

/**
 * One reason the batch was refused. Every row is validated before ANY row is
 * written, so a blocker list means NOTHING was published — never present it as
 * a partial success.
 *
 * Each blocker carries the `base`/`quote` it refers to so the panel can
 * highlight the row. Branch on `code`, never on `message`.
 */
export interface FxRatesBlocker {
  code: FxRatesBlockerCode | string;
  base?: string;
  quote?: string;
  /** `UNKNOWN_CURRENCY` — which of the two is not an active currency. */
  currency?: string;
  /** `REVERSE_PAIR_EXISTS` — the current rate that already exists the other way round. */
  existing?: AppliedFxRate | string;
  message?: string;
  [key: string]: unknown;
}

/**
 * `ERROR_SYSTEM` payload. Validating up front removes every foreseeable failure
 * before the first write, but the publish loop is NOT a database transaction:
 * an infrastructure failure part way through leaves `applied` published and
 * `failedAt` not. Re-submit the remainder.
 */
export interface FxRatesBatchPartialFailure {
  applied?: AppliedFxRate[];
  failedAt?: FxRateRow;
}

/**
 * GBX rows are derived from GBP at read time and are never stored. Publishing a
 * real GBX row would suppress the derived one, so it is not importable at all —
 * publish GBP and let the backend derive.
 */
export const NON_IMPORTABLE_CURRENCIES = ['GBX'];

export function isNonImportableCurrency(code: string | null | undefined): boolean {
  return NON_IMPORTABLE_CURRENCIES.includes(String(code ?? '').trim().toUpperCase());
}

/**
 * Strips a parsed or edited row down to the four fields the batch endpoint
 * accepts, and normalises the currency codes the way the backend would.
 *
 * Any key beyond these four is rejected outright rather than dropped — which is
 * deliberate, so a typo fails loudly instead of publishing something
 * unintended. That is exactly why payloads are built through here and never by
 * spreading a parsed pair.
 */
export function toSubmittableFxRate(row: {
  base?: string | null;
  quote?: string | null;
  bid?: number | string | null;
  ask?: number | string | null;
}): FxRateRow {
  return {
    base: String(row.base ?? '').trim().toUpperCase(),
    quote: String(row.quote ?? '').trim().toUpperCase(),
    bid: Number(row.bid),
    ask: Number(row.ask),
  };
}

@Injectable({ providedIn: 'root' })
export class FxRatesImportService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`, so these resolve to `/api/admin/fxrates/...`. */
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  /** Nothing is persisted. Re-upload as often as you like. */
  parse(payload: ParseFxRatesPayload): Observable<ParseFxRatesResponse> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/fxrates/parse`,
      payload
    );
  }

  /**
   * Publishes the whole book in one call. Each pair goes through the same
   * `setRate` the single-rate endpoint uses: the previous row is flipped to
   * `isCurrent: false` and a NEW row is inserted. So after a batch the same
   * pairs come back with NEW ids — do not cache rate ids across a publish.
   */
  batch(payload: BatchFxRatesPayload): Observable<BatchFxRatesResponse> {
    return this.networkHelper.post(`${this.hostUrl}/admin/fxrates/batch`, {
      rates: (payload.rates || []).map(toSubmittableFxRate),
    });
  }
}
